import { ReactElement, useEffect, useMemo, useState } from 'react';
import { grievancesAPI } from '../../services/api';


type GrievanceRow = {
  id: number;
  grievanceId: string;
  subject?: string;
  description?: string;
  status: string;
  priority?: string;
  createdAt: string;
};

// Simple donut chart using SVG (no external deps)
function DonutChart({ segments, total, colors, labels, title }: { segments: number[]; total: number; colors: string[]; labels: string[]; title: string; }) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const normalized = total > 0 ? segments.map(v => (v / total) * circumference) : segments.map(() => 0);
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-sm text-gray-600 mb-2">{title}</div>
      <div className="flex items-center gap-6">
        <svg width="160" height="160" viewBox="0 0 160 160">
          <g transform="translate(80,80)">
            {normalized.map((len, i) => {
              const circle = (
                <circle
                  key={i}
                  r={radius}
                  cx={0}
                  cy={0}
                  fill="transparent"
                  stroke={colors[i]}
                  strokeWidth={20}
                  strokeDasharray={`${len} ${circumference - len}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += len;
              return circle;
            })}
            <circle r={40} cx={0} cy={0} fill="#fff" />
            <text textAnchor="middle" dy="5" className="fill-gray-800 text-sm">
              {total}
            </text>
          </g>
        </svg>
        <div className="space-y-2">
          {segments.map((v, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: colors[i] }} />
              <span className="text-gray-600">{labels[i]}:</span>
              <span className="font-semibold text-gray-900">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Simple bar chart using divs
function BarChart({ data, title }: { data: { label: string; value: number }[]; title: string; }) {
  const max = Math.max(1, ...data.map(d => d.value));
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-sm text-gray-600 mb-3">{title}</div>
      <div className="grid grid-cols-12 gap-2 items-end h-48">
        {data.map((d, i) => (
          <div key={i} className="flex flex-col items-center justify-end">
            <div className="bg-blue-500 w-4 rounded" style={{ height: `${(d.value / max) * 100}%` }} title={`${d.label}: ${d.value}`} />
            <div className="text-[10px] mt-1 text-gray-600 rotate-45 origin-top-left h-8 overflow-visible">{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const AdminReports = (): ReactElement => {
  const [rows, setRows] = useState<GrievanceRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await grievancesAPI.getAll();
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byStatus = useMemo(() => {
    const counts: Record<string, number> = { pending: 0, 'in-progress': 0, resolved: 0};
    rows.forEach(r => { const k = (r.status || '').toLowerCase(); if (counts[k] !== undefined) counts[k]++; });
    return counts;
  }, [rows]);

  const byPriority = useMemo(() => {
    const counts: Record<string, number> = { LOW: 0, MEDIUM: 0, HIGH: 0 };
    rows.forEach(r => { const k = (r.priority || '').toUpperCase(); if (counts[k] !== undefined) counts[k]++; });
    return counts;
  }, [rows]);

  const perMonth = useMemo(() => {
    // Last 6 months including current
    const map = new Map<string, number>();
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map.set(key, 0);
    }
    rows.forEach(r => {
      const d = new Date(r.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (map.has(key)) map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(([k, v]) => ({ label: k, value: v }));
  }, [rows]);

  const total = rows.length;
  const statusSegments = [byStatus.pending, byStatus['in-progress'], byStatus.resolved];
  const statusLabels = ['Pending', 'In Progress', 'Resolved'];
  const statusColors = ['#f59e0b', '#3b82f6', '#10b981'];
  const prioritySegments = [byPriority.LOW, byPriority.MEDIUM, byPriority.HIGH];
  const priorityLabels = ['Low', 'Medium', 'High'];
  const priorityColors = ['#60a5fa', '#f59e0b', '#ef4444'];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <button onClick={load} className="px-3 py-2 text-sm rounded border hover:bg-gray-50">{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <DonutChart segments={statusSegments} total={total} colors={statusColors} labels={statusLabels} title="By Status" />
        <DonutChart segments={prioritySegments} total={total} colors={priorityColors} labels={priorityLabels} title="By Priority" />
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-2">Summary</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Total</span><span className="font-semibold">{total}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Pending</span><span className="font-semibold">{byStatus.pending}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">In Progress</span><span className="font-semibold">{byStatus['in-progress']}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Resolved</span><span className="font-semibold">{byStatus.resolved}</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <BarChart data={perMonth} title="Submissions (last 6 months)" />
      </div>
    </div>
  );
};

export default AdminReports;
